/**
 * Демо-сцена героя: «одно окно» + один объект в хранении + агент снаружи.
 *
 * Раскладка целиком берётся из `demo-scene-layout.ts` — здесь только рендер.
 * Ни одной координаты зоны в этом файле быть не должно: если понадобилось
 * подвинуть окно, правится таблица ZONES, а рёбра, пунктиры и кадры камеры
 * пересчитываются сами.
 *
 * Что читается в композиции:
 *   • рамка PRONA обводит продуктовые окна — они разделы ОДНОГО окна;
 *   • агент стоит СНАРУЖИ рамки и связан с ней единственной линией через MCP;
 *   • сквозных артефактов ДВА — документ «Корзина: как устроено сейчас» и сама
 *     задача SHOP-214. Каждый лежит в нескольких окнах сразу, и от каждого
 *     вхождения пунктир уходит вниз в узел хранения: это не копии, а один
 *     объект в разных представлениях.
 *
 * Артефакты показывают СОДЕРЖИМОЕ, а не название: документ на канве — лист с
 * телом, задача — карточка с ключом, исполнителем и приоритетом. Узнавание
 * идёт по паре «значок раздела + название», одинаковой во всех окнах.
 */
import React from 'react';
import {
  WORLD, FRAME, ZONES, MCP_GATE, STORE_LINKS, type Zone,
} from './demo-scene-layout';
import { buildTimelineCss } from './demo-scene-timeline';

const INK = '#12241d';
const MUTED = '#6f7a74';
const LINE = 'rgba(15,23,42,.10)';
const CARD = '#fff';
/**
 * Сквозных артефактов в сцене ДВА, и каждый носит цвет своего раздела:
 * документ — знания-золото, задача — задачи-индиго (см. TASKS ниже). Не общий
 * «цвет артефакта»: тогда две семьи пунктиров сливаются в одну кашу, а так
 * пользователь заодно запоминает цветовую инфографику разделов.
 */
const ART = '#b08a3e';
const ART_BG = 'rgba(176,138,62,.12)';
const ART_BD = 'rgba(176,138,62,.50)';

/**
 * Токены разделов — ровно те, что в `styles/variables.scss`. Сцена не выдумывает
 * палитру: пользователь встретит эти же оттенки в продукте, и узнавание должно
 * начинаться с лендинга.
 */
const TASKS = { accent: '#5454a1', muted: '#7878b6', light: '#e3e3f0', bg: '#f4f4f9' };
const KNOW = { accent: '#b08a3e', muted: '#cbab73', light: '#f1e7c8', bg: '#faf5e6' };
/** Чат-нейтраль: своя slate-палитра, не наследует акцент раздела. */
const CHAT = { accent: '#5b6b80', selfBg: '#eef1f5', selfBorder: 'rgba(60,80,110,.14)' };
/** Палитра тела документа (pronova-tokens.docColors) — для страницы в БЗ. */
const DOC = { ink: '#1A1A1C', mut: '#6B6862', line: '#E0DCD5', zebra: '#F5F3EF' };
/** Нейтрали списков задач — из MultiProjectBacklogView.module.scss. */
const ROW = { border: '#f0f0f0', key: 'rgba(0,0,0,.45)', title: 'rgba(0,0,0,.85)', meta: 'rgba(0,0,0,.55)' };

/** Общая обёртка окна продукта: белая карточка со скруглением и тонкой рамкой. */
function Window({
  zone, children, chrome = true, dim = false, cls, dock = false, z,
}: {
  zone: Zone; children?: React.ReactNode; chrome?: boolean; dim?: boolean; cls?: string;
  /**
   * Место в стопке. Нужно ровно одному окну — чат-сайдбару: он обязан лечь
   * ВЫШЕ курсоров команды (панель закрывает доску вместе с теми, кто на ней
   * работал) и НИЖЕ курсора агента — тот в этот момент внутри панели и должен
   * быть виден. Порядком в разметке этого не добиться: z-index сравнивается со
   * всей стопкой сразу, а не с соседями.
   */
  z?: number;
  /**
   * Сайдбар, пристыкованный к ПРАВОЙ кромке другого окна: скругление только
   * слева, тень падает влево — панель читается как выехавшая сбоку створка, а
   * не как ещё одна карточка, случайно легшая поверх.
   */
  dock?: boolean;
}) {
  return (
    <div
      className={cls}
      style={{
        position: 'absolute', left: zone.x, top: zone.y, width: zone.w, height: zone.h,
        background: CARD, border: `1px solid ${LINE}`,
        borderRadius: dock ? '13px 0 0 13px' : 14,
        opacity: dim ? 0.55 : 1, overflow: 'hidden',
        ...(z !== undefined ? { zIndex: z } : null),
        ...(dock ? { boxShadow: '-16px 0 42px -20px rgba(16,35,28,.55)' } : null),
      }}
    >
      {chrome && (
        <div style={{
          height: 30, borderBottom: `1px solid ${LINE}`, display: 'flex', alignItems: 'center',
          padding: '0 12px', gap: 7, background: '#fbfaf8',
        }}>
          {/* Знак раздела — тот же, что стоит фавиконкой во вкладке браузера.
              Так цветовая инфографика разделов узнаётся ещё до первого входа. */}
          {/* Знак всегда КРУГЛЫЙ: одинаковая форма у всех разделов делает
              различимым именно цвет — на него и приучаем смотреть. */}
          {zone.icon ? (
            <span style={{
              // Рамки у обёртки НЕТ намеренно: знак — full-bleed градиент, и
              // рамка без box-sizing давала бокс 18px под картинку 16px, отчего
              // знак съезжал внутри круга. Картинка тянется на всю обёртку.
              width: 16, height: 16, borderRadius: '50%', overflow: 'hidden',
              flex: 'none', display: 'block',
            }}>
              {/* eslint-disable-next-line @next/next/no-img-element -- сцена, не контентная картинка */}
              <img src={zone.icon} alt="" style={{ display: 'block', width: '100%', height: '100%' }} />
            </span>
          ) : (
            <span style={{ width: 16, height: 16, borderRadius: '50%', background: zone.accent ?? '#d9d5cc', flex: 'none' }} />
          )}
          <span style={{ fontSize: 11, fontWeight: 600, color: zone.accent ?? MUTED, letterSpacing: '.02em' }}>
            {zone.title}
          </span>
        </div>
      )}
      <div style={{ position: 'relative', height: chrome ? zone.h - 30 : zone.h }}>{children}</div>
    </div>
  );
}

/** Строка-заглушка контента: полоска нужной ширины. */
function Bar({ w, top, left = 12, tone = LINE }: { w: number; top: number; left?: number; tone?: string }) {
  return <div style={{ position: 'absolute', left, top, width: w, height: 6, borderRadius: 3, background: tone }} />;
}

/** Значок чата — пузырь реплики. */
function ChatGlyph({ color, size = 10 }: { color: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" style={{ display: 'block', flex: 'none' }}>
      <path d="M1.2 2.2h9.6v6.2H5.2L2.6 10.6V8.4H1.2z" fill="none" stroke={color} strokeWidth="1.1" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Аватар участника: либо инициалы на цветном круге, либо знак агента.
 * Размер 18 — тот же, что у `.itemAvatar` в бэклоге.
 */
function Ava({ n, color, src, size = 18 }: { n?: string; color?: string; src?: string; size?: number }) {
  if (src) {
    return (
      // eslint-disable-next-line @next/next/no-img-element -- сцена, не контентная картинка
      <img src={src} alt="" style={{ width: size, height: size, borderRadius: '50%', display: 'block', flex: 'none' }} />
    );
  }
  return (
    <span style={{
      width: size, height: size, borderRadius: '50%', background: color ?? '#d9d9d9', flex: 'none',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.44, fontWeight: 600, color: '#fff', letterSpacing: '.01em',
    }}>{n}</span>
  );
}

/** Иконка приоритета — те же файлы `public/issue_priority/*`, что в бэклоге. */
function Prio({ level, size = 11 }: { level: 'highest' | 'high' | 'medium' | 'low'; size?: number }) {
  const file = { highest: 'highest.svg', high: 'high_new.svg', medium: 'medium_new.svg', low: 'low_new.svg' }[level];
  return (
    // eslint-disable-next-line @next/next/no-img-element -- сцена, не контентная картинка
    <img src={`/issue_priority/${file}`} alt="" style={{ width: size, height: size, display: 'block', flex: 'none' }} />
  );
}

/** Чекбокс строки бэклога — форма antd Checkbox в масштабе сцены. */
function Check({ on = false }: { on?: boolean }) {
  return (
    <span style={{
      width: 9, height: 9, borderRadius: 2, flex: 'none',
      border: `1px solid ${on ? TASKS.accent : '#d9d9d9'}`, background: on ? TASKS.accent : '#fff',
    }} />
  );
}

/**
 * Строка бэклога — повторяет `.itemRow` из MultiProjectBacklogView: чекбокс,
 * ключ моноширинным, название, справа приоритет / story points / исполнитель.
 */
function BacklogRow({
  top, k, title, prio, sp, ava, checked, dim, cls, glow, avaCls,
}: {
  top: number; k: string; title: string; prio: 'highest' | 'high' | 'medium' | 'low';
  sp?: string; ava: { n?: string; color?: string; src?: string }; checked?: boolean; dim?: boolean;
  cls?: string; glow?: string; avaCls?: string;
}) {
  return (
    <div className={cls} style={{
      ...(glow ? { ['--glow' as string]: glow } : null),
      position: 'absolute', left: 10, top, right: 10, height: 24,
      display: 'flex', alignItems: 'center', gap: 6, padding: '0 7px',
      background: '#fff', border: `1px solid ${ROW.border}`, borderRadius: 5, opacity: dim ? 0.72 : 1,
    }}>
      <Check on={checked} />
      <span style={{ fontFamily: 'var(--pn-mono)', fontSize: 8, color: ROW.key, flex: 'none', minWidth: 44 }}>{k}</span>
      <span style={{
        fontSize: 9.5, color: ROW.title, flex: 1, minWidth: 0,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{title}</span>
      <Prio level={prio} />
      {sp && (
        <span style={{
          fontSize: 8, color: ROW.meta, fontVariantNumeric: 'tabular-nums',
          minWidth: 12, textAlign: 'center',
        }}>{sp}</span>
      )}
      <span className={avaCls} style={{ position: 'relative', display: 'flex', flex: 'none' }}>
        {ava.src && <Ripple left={7} top={7} keys={RIP_ASSIGN} />}
        <Ava {...ava} size={14} />
      </span>
    </div>
  );
}

/**
 * Заголовок секции бэклога — как `.sectionHeader` в multi-mode: шеврон, пилюля
 * имени спринта, цель курсивом через «·», счётчик справа.
 */
function SectionHead({
  top, name, goal, count, active, cls, counts,
}: {
  top: number; name: string; goal?: string; count?: string; active?: boolean; cls?: string;
  /**
   * Два счётчика вместо одного: когда в спринт вставляется задача, «5 задач»
   * должно смениться на «6 задач». Спаны лежат друг на друге и подменяются
   * тактами — иначе на экране 6 строк при подписи «5 задач», а это в демо
   * продукта видимая неправда.
   */
  counts?: { a: string; b: string; aCls: string; bCls: string };
}) {
  return (
    <div className={cls} style={{
      position: 'absolute', left: 10, top, right: 10, height: 18,
      display: 'flex', alignItems: 'center', gap: 6, padding: '0 6px',
      background: '#fafbfc', borderRadius: 4,
    }}>
      <span style={{ fontSize: 7, color: ROW.meta }}>▾</span>
      <span style={{
        fontSize: 8, fontWeight: 600, borderRadius: 999, padding: '1px 7px', whiteSpace: 'nowrap',
        ...(active
          ? { color: TASKS.accent, background: TASKS.bg, border: `1px solid ${TASKS.light}` }
          : { color: ROW.meta, background: '#f2f3f5', border: `1px solid ${ROW.border}` }),
      }}>{name}</span>
      {goal && (
        <span style={{
          fontSize: 8, color: MUTED, minWidth: 0, overflow: 'hidden',
          textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>· {goal}</span>
      )}
      {counts ? (
        <span style={{ marginLeft: 'auto', position: 'relative', fontSize: 8, color: ROW.meta, whiteSpace: 'nowrap' }}>
          <span className={counts.aCls}>{counts.a}</span>
          <span className={counts.bCls} style={{ position: 'absolute', right: 0, top: 0 }}>{counts.b}</span>
        </span>
      ) : (
        <span style={{ marginLeft: 'auto', fontSize: 8, color: ROW.meta, whiteSpace: 'nowrap' }}>{count}</span>
      )}
    </div>
  );
}

/** Поле карточки задачи: метка слева, значение в поле-селекте справа. */
function Field({
  top, label, children, cls, glow,
}: { top: number; label: string; children: React.ReactNode; cls?: string; glow?: string }) {
  return (
    <div style={{ position: 'absolute', left: 12, top, right: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 8.5, color: MUTED, width: 62, flex: 'none' }}>{label}</span>
      <span className={cls} style={{
        ...(glow ? { ['--glow' as string]: glow } : null),
        position: 'relative',
        flex: 1, minWidth: 0, height: 20, display: 'flex', alignItems: 'center', gap: 5,
        padding: '0 7px', border: `1px solid ${LINE}`, borderRadius: 5, background: '#fff',
        fontSize: 9, color: INK,
      }}>{children}</span>
    </div>
  );
}

/**
 * Расходящиеся круги — три кольца с разбегом от одной точки.
 *
 * Служат двум разным вещам, и обе — про «здесь произошло действие»:
 *   • назначение исполнителя: кольца бьют СРАЗУ В ТРЁХ местах (поле задачи,
 *     карточка доски, строка бэклога). Иначе смена исполнителя видна только как
 *     подмена мелкого аватара; кольца тянут к ней взгляд и лишний раз
 *     показывают, что запись одна. Цвет — «глиняный» оранжевый знака агента;
 *   • нажатие: клик по кнопке иначе не показать вовсе — курсор просто стоит на
 *     месте, и «нажал» неотличимо от «подвёл».
 *
 * Классы передаются снаружи: у каждой тройки колец свой момент, а один такт
 * хореографии обслуживает ровно один селектор.
 */
function Ripple({
  left, top, keys, color = '#D97757', size = 26,
}: { left: number; top: number; keys: readonly [string, string, string]; color?: string; size?: number }) {
  return (
    <>
      {keys.map((cls) => (
        <span key={cls} className={cls} style={{
          position: 'absolute', left, top, width: size, height: size,
          marginLeft: -size / 2, marginTop: -size / 2,
          borderRadius: '50%', border: `1.6px solid ${color}`, pointerEvents: 'none', opacity: 0,
        }} />
      ))}
    </>
  );
}

/** Тройки колец: назначение исполнителя, нажатие «Создать задачу», клик по чату. */
const RIP_ASSIGN = ['dm-rip-1', 'dm-rip-2', 'dm-rip-3'] as const;
const RIP_CREATE = ['dm-rip-c1', 'dm-rip-c2', 'dm-rip-c3'] as const;
const RIP_CHAT = ['dm-rip-h1', 'dm-rip-h2', 'dm-rip-h3'] as const;

/**
 * Знаки разделов — те же файлы, что стоят в шапках окон сцены и в ленте разделов
 * на главной. Здесь они помечают ИСТОЧНИК артефакта в списке связанных: строка
 * сразу говорит, откуда вещь пришла — из Базы знаний, с доски или из переписки.
 * Прежде там стояли обезличенные глифы «документ / доска / чат»: тип они
 * называли, а раздел — нет, хотя вся сцена про то, что артефакт живёт в разделах.
 */
const SRC_MARK = {
  know: '/prona-knowledge-section-chrome.svg',
  board: '/prona-board-section-chrome.svg',
  chat: '/prona-chat-section-chrome.svg',
} as const;

/** Круглый знак источника в строке артефакта. */
function SourceMark({ src, size = 12 }: { src: string; size?: number }): React.ReactElement {
  return (
    // eslint-disable-next-line @next/next/no-img-element -- знак раздела, не контентная картинка
    <img src={src} alt="" width={size} height={size} style={{
      width: size, height: size, borderRadius: '50%', display: 'block', flex: 'none',
    }} />
  );
}

/** Значок документа — страница со скруглённым уголком, в цвет раздела. */
function DocGlyph({ color, size = 9 }: { color: string; size?: number }) {
  return (
    <svg width={size} height={size * 1.2} viewBox="0 0 10 12" style={{ display: 'block', flex: 'none' }}>
      <path d="M1 1h5l3 3v7H1z" fill="none" stroke={color} strokeWidth="1.1" strokeLinejoin="round" />
      <path d="M6 1v3h3" fill="none" stroke={color} strokeWidth="1.1" strokeLinejoin="round" />
    </svg>
  );
}

/** Строка дерева БЗ: папка или документ, активная — подложкой знаний-золота. */
function TreeRow({
  indent, label, folder, open, active,
}: { indent: number; label: string; folder?: boolean; open?: boolean; active?: boolean }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 4, height: 17,
      paddingLeft: 3 + indent * 8, paddingRight: 3, borderRadius: 4,
      background: active ? KNOW.bg : 'transparent',
      border: `1px solid ${active ? KNOW.muted : 'transparent'}`,
    }}>
      {folder ? (
        <>
          <span style={{ fontSize: 6, color: KNOW.accent }}>{open ? '▾' : '▸'}</span>
          <svg width="9" height="8" viewBox="0 0 10 9" style={{ display: 'block', flex: 'none' }}>
            <path d="M1 8V1h3l1.2 1.4H9V8z" fill={KNOW.light} stroke={KNOW.accent} strokeWidth="1" strokeLinejoin="round" />
          </svg>
        </>
      ) : (
        <DocGlyph color={active ? KNOW.accent : KNOW.muted} size={8} />
      )}
      <span style={{
        fontSize: 8, lineHeight: 1.2, color: active ? KNOW.accent : DOC.ink,
        fontWeight: active || folder ? 600 : 400,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{label}</span>
    </div>
  );
}

/**
 * Палитра стикеров доски — насыщённая, как на канве, а не приглушённая.
 * Пары взяты из дизайн-системы (variables.scss) и подняты по насыщенности до
 * плотности бумажного стикера: приглушённые оттенки на канве читались как
 * бледные карточки, а не как наклеенный листок.
 */
const STICKY = {
  gold: '#F7DF6A',
  indigo: '#CFCBF2',
  green: '#B4E5C8',
  shell: '#ABDCE5',
} as const;

/**
 * Стикер доски.
 *
 * Форма скопирована с настоящего: БЕЗ рамки, почти острые углы, загнутый нижний
 * правый угол и мягкая тень. Скруглённая карточка с бордюром читалась как
 * элемент интерфейса, а не как наклеенный на канву листок — а именно это и надо
 * показать. Загиб рисуется двумя треугольниками: светлый «отогнутый» лист и
 * тень под ним.
 */
function Sticky({
  left, top, w, h, tone, children, cls,
}: {
  left: number; top: number; w: number; h: number;
  tone: keyof typeof STICKY; children: React.ReactNode; cls?: string;
}) {
  const FOLD = 18;
  return (
    <div className={cls} style={{
      position: 'absolute', left, top, width: w, height: h, padding: '15px 14px 13px',
      background: STICKY[tone], borderRadius: 2,
      boxShadow: '0 8px 16px -10px rgba(16,35,28,.5)',
      fontSize: 11.5, lineHeight: 1.45, color: '#3f3a2a', fontWeight: 500,
      clipPath: `polygon(0 0, 100% 0, 100% calc(100% - ${FOLD}px), calc(100% - ${FOLD}px) 100%, 0 100%)`,
    }}>
      {children}
      {/* Загнутый уголок: тень листка под отгибом. */}
      <span style={{
        position: 'absolute', right: 0, bottom: 0, width: FOLD, height: FOLD,
        background: 'rgba(0,0,0,.10)',
        clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
      }} />
    </div>
  );
}

/** Мини-таблица — один из элементов доски. */
function MiniTable({ left, top, w }: { left: number; top: number; w: number }) {
  const rows: [string, string][] = [['< 24 ч', '82%'], ['1–7 дн', '13%'], ['> 7 дн', '5%']];
  return (
    <div style={{ position: 'absolute', left, top, width: w, background: '#fff', border: `1px solid ${LINE}`, borderRadius: 7, overflow: 'hidden' }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.06em', color: MUTED, padding: '6px 10px', background: '#f4f2ee' }}>
        СРОК ЖИЗНИ КОРЗИН
      </div>
      {rows.map(([a, b], i) => (
        <div key={a} style={{
          display: 'flex', justifyContent: 'space-between', padding: '5px 10px',
          fontSize: 11, color: INK, borderTop: i ? `1px solid ${LINE}` : 'none',
          fontVariantNumeric: 'tabular-nums',
        }}>
          <span>{a}</span><span style={{ fontWeight: 600 }}>{b}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Схема связей — рисуется ПО ЧАСТЯМ: блок, стрелка, блок, стрелка, блок.
 * Каждый кусок носит свой класс, поэтому такты выкладывают её так, как её
 * строит человек: сначала объект, потом связь, потом следующий объект. Одним
 * куском схема «возникала», и было непонятно, что её кто-то рисует.
 */
function MiniScheme({ left, top }: { left: number; top: number }) {
  const box = (x: number, label: string, tone: string, cls: string) => (
    <div className={cls} style={{
      position: 'absolute', left: x, top: 16, width: 62, padding: '6px 0',
      textAlign: 'center' as const, background: '#fff', border: `1px solid ${tone}`,
      borderRadius: 6, fontSize: 10, fontWeight: 600, color: INK,
    }}>{label}</div>
  );
  const arrow = (x: number, cls: string) => (
    <svg className={cls} style={{ position: 'absolute', left: x, top: 26, width: 26, height: 12 }} viewBox="0 0 26 12">
      <path d="M0 6 L18 6" stroke="#9aa5a0" strokeWidth="1.4" />
      <path d="M18 2 L24 6 L18 10 z" fill="#9aa5a0" />
    </svg>
  );
  return (
    <div style={{ position: 'absolute', left, top, width: 246, height: 62 }}>
      <div className="dm-sch-lbl" style={{
        position: 'absolute', left: 0, top: 0, fontSize: 10, fontWeight: 700,
        letterSpacing: '.06em', color: MUTED,
      }}>
        НА ЧТО ВЛИЯЕТ
      </div>
      {box(0, 'Корзина', '#7878b6', 'dm-sch-b1')}
      {arrow(64, 'dm-sch-a1')}
      {box(92, 'Заказ', '#7cbaa0', 'dm-sch-b2')}
      {arrow(156, 'dm-sch-a2')}
      {box(184, 'Остатки', '#cbab73', 'dm-sch-b3')}
    </div>
  );
}

/**
 * Документ НА ДОСКЕ — не плашка с названием, а лист с содержимым: на канве
 * документ всегда показывает своё тело. Текст тот же, что на странице в БЗ, —
 * это и делает очевидным, что объект один.
 */
function BoardDocCard({ left, top, w, h, cls, glow }: { left: number; top: number; w: number; h: number; cls?: string; glow?: string }) {
  return (
    <div className={cls} style={{
      ...(glow ? { ['--glow' as string]: glow } : null),
      position: 'absolute', left, top, width: w, height: h, overflow: 'hidden',
      background: '#fff', border: `1.2px solid ${ART}`, borderRadius: 8,
      boxShadow: '0 4px 10px -7px rgba(16,35,28,.4)', fontFamily: 'var(--pn-doc)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
        background: ART_BG, borderBottom: `1px solid ${ART_BD}`,
      }}>
        <DocGlyph color={ART} size={11} />
        <span style={{ fontSize: 11, fontWeight: 600, color: ART }}>{ARTIFACT}</span>
      </div>
      <div style={{ padding: '8px 10px', color: DOC.ink }}>
        <p style={{ fontSize: 9, lineHeight: 1.55, margin: '0 0 6px' }}>
          Корзина живёт 24 часа с последнего изменения. По истечении срока позиции
          удаляются, резерв остатков снимается.
        </p>
        <div style={{ fontSize: 9.5, fontWeight: 600, margin: '0 0 3px' }}>Что зависит от срока</div>
        {['Резерв остатков на складе', 'Расчёт стоимости доставки'].map((t) => (
          <div key={t} style={{ display: 'flex', gap: 5, fontSize: 9, lineHeight: 1.5 }}>
            <span style={{ color: DOC.mut }}>•</span><span>{t}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Задача НА ДОСКЕ — карточка с превью: ключ, название, исполнитель, приоритет.
 * Ровно те же ключ и аватар, что в строке бэклога и в окне задачи: по ним видно,
 * что это одна запись в трёх местах, а не три одноимённые карточки.
 */
function BoardTaskCard({ left, top, w, h, cls, glow }: { left: number; top: number; w: number; h: number; cls?: string; glow?: string }) {
  return (
    <div className={cls} style={{
      ...(glow ? { ['--glow' as string]: glow } : null),
      position: 'absolute', left, top, width: w, height: h, overflow: 'hidden',
      background: '#fff', border: `1.4px solid ${TASKS.accent}`, borderRadius: 9,
      boxShadow: '0 6px 14px -9px rgba(16,35,28,.45)', padding: '9px 11px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <span style={{
          fontFamily: 'var(--pn-mono)', fontSize: 9.5, color: TASKS.accent,
          background: TASKS.bg, border: `1px solid ${TASKS.light}`, borderRadius: 4, padding: '1px 6px',
        }}>SHOP-214</span>
        <Prio level="high" size={11} />
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: INK, lineHeight: 1.3, margin: '7px 0 0' }}>
        Хранить корзину 30 дней
      </div>
      <div style={{ fontSize: 9.5, color: MUTED, lineHeight: 1.4, marginTop: 3 }}>
        собрана из выделенных элементов доски
      </div>
      {/* Два состояния подвала: пока исполнителя нет — и после назначения.
          Карточка на доске обязана отставать ровно так же, как страница задачи:
          иначе на 34-й секунде на доске уже стоит агент, которого назначат
          только на 37-й, и зритель видит противоречие. */}
      <div className="dm-b-task-empty" style={{ position: 'absolute', left: 7, right: 7, bottom: 5, display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ width: 14, height: 14, borderRadius: '50%', border: `1px dashed ${MUTED}`, flex: 'none' }} />
        <span style={{ fontSize: 9.5, color: MUTED }}>исполнитель не назначен</span>
        <span style={{
          marginLeft: 'auto', fontSize: 9, fontWeight: 600, color: ROW.meta,
          background: '#f2f3f5', border: `1px solid ${ROW.border}`, borderRadius: 999, padding: '1px 6px',
        }}>Бэклог</span>
      </div>
      <div className="dm-b-task-agent" style={{ position: 'absolute', left: 7, right: 7, bottom: 5, display: 'flex', alignItems: 'center', gap: 4 }}>
        <Ripple left={7} top={7} keys={RIP_ASSIGN} />
        <Ava src="/claude-code-icon.svg" size={14} />
        <span style={{ fontSize: 9.5, color: INK }}>Claude Code</span>
        <span style={{
          marginLeft: 'auto', fontSize: 9, fontWeight: 600, color: TASKS.accent,
          background: TASKS.bg, border: `1px solid ${TASKS.light}`, borderRadius: 999, padding: '1px 6px',
        }}>В работе</span>
      </div>
    </div>
  );
}

/**
 * Курсор участника с именной плашкой и репликой — как на живой доске.
 * Реплики и есть суть сцены: команда полгода спустя не помнит, откуда взялась
 * тема, и спрашивает друг друга. Агент отвечает последним.
 */
function Cursor({
  x, y, name, color, cls, says = [], agent = false, icon, z = 40,
}: {
  /** Мировые координаты: курсор живёт ВНЕ окна доски. */
  x: number; y: number; name: string; color: string; cls: string;
  /**
   * Место в стопке. Люди стоят НИЖЕ чат-сайдбара (40 против 45): выехавшая
   * панель закрывает доску вместе с теми, кто на ней работал, — иначе чужие
   * курсоры «плавают» поверх переписки, к которой отношения не имеют. Агент —
   * выше (50): он в этот момент внутри панели и ищет там ответ.
   */
  z?: number;
  /**
   * Реплики этого участника — все на одном месте, показываются по очереди.
   * Списком, а не отдельными курсорами: два курсора с одним именем пришлось бы
   * гасить и зажигать встык, и плашка имени мигала бы на стыке.
   */
  says?: { cls: string; text: string; w: number }[];
  agent?: boolean;
  /** Значок в плашке имени — для агентов, чтобы отличались от людей сразу. */
  icon?: string;
}) {
  return (
    <div className={cls} style={{
      // Курсоры лежат ПОВЕРХ окон и вне их: у окна `overflow: hidden`, и внутри
      // курсор упирался в кромку — а он должен уметь выйти за неё, например
      // когда Лена тянет документ из соседней панели на доску.
      position: 'absolute', left: x, top: y, zIndex: z, pointerEvents: 'none',
    }}>
      <svg width="17" height="19" viewBox="0 0 13 15" style={{ display: 'block', filter: 'drop-shadow(0 2px 4px rgba(16,35,28,.35))' }}>
        <path d="M1 1 L1 12.5 L4.2 9.6 L6.4 14 L8.6 13 L6.5 8.7 L11 8.4 Z" fill={color} stroke="#fff" strokeWidth="1" />
      </svg>
      <span style={{
        position: 'absolute', left: 13, top: 14, padding: '2px 8px', borderRadius: 999,
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: color, color: '#fff', fontSize: 10.5, fontWeight: 600, whiteSpace: 'nowrap',
      }}>
        {icon && (
          // eslint-disable-next-line @next/next/no-img-element -- сцена, не контентная картинка
          <img src={icon} alt="" style={{ width: 11, height: 11, borderRadius: '50%', display: 'block' }} />
        )}
        {name}
      </span>
      {says.map((s) => (
        <div key={s.cls} className={s.cls} style={{
          position: 'absolute', left: 20, top: -6, width: 'max-content', maxWidth: s.w,
          whiteSpace: 'nowrap',
          // Шторка стоит справа до начала набора; ширина у пузыря естественная,
          // поэтому текст физически не может обрезаться.
          clipPath: 'inset(0 100% 0 0)',
          padding: '5px 0', borderRadius: 9,
          background: agent ? '#101613' : '#fff',
          boxShadow: `0 0 0 1px ${agent ? 'rgba(255,255,255,.14)' : LINE}, 0 6px 14px -9px rgba(16,35,28,.5)`,
          fontSize: 11, lineHeight: 1.3, color: agent ? '#e6efe9' : INK,
        }}>
          <span style={{ padding: '0 10px', display: 'inline-block' }}>{s.text}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Реплика участника — ЧАСТЬ КУРСОРА, справа от стрелки.
 *
 * Раньше пузыри стояли на канве отдельно, и курсор к ним подходил. Но это не
 * разговор, а расставленные таблички: у живой доски реплика едет вместе с тем,
 * кто её написал. Текст набирается побуквенно (такт kind:'type' — ширина растёт
 * ступенями по числу знаков), поэтому видно именно обмен сообщениями.
 *
 * Реплика ОДНОСТРОЧНАЯ: побуквенный набор через ширину работает только на одной
 * строке — на переносе слово прыгнет на следующую целиком.
 */
/**
 * Рамка выделения на доске и всплывающая кнопка действия.
 *
 * Это ключевой кадр всей сцены: задача не возникает сама, её СОЗДАЮТ из
 * выделенных элементов доски. Поэтому рамка обводит ровно те элементы, что
 * станут связанными артефактами задачи, — зритель видит причину и следствие.
 */
function Lasso({
  left, top, w, h, cls, btnCls,
}: { left: number; top: number; w: number; h: number; cls: string; btnCls: string }) {
  return (
    <>
      <div className={cls} style={{
        // Начало координат — левый верхний угол: такт 'draw' тянет рамку
        // вправо-вниз вслед за курсором, как при протяжке мышью.
        transformOrigin: '0 0',
        position: 'absolute', left, top, width: w, height: h,
        border: `1.4px dashed ${TASKS.accent}`, borderRadius: 6,
        background: 'rgba(84,84,161,.06)', pointerEvents: 'none',
      }} />
      {/* Кольца нажатия лежат ПОД кнопкой и в её цвете: середину кнопка
          закрывает, наружу кольца выходят на канву — читается как удар от
          точки нажатия, а не как подсветка самой кнопки. */}
      <div className={btnCls} style={{
        position: 'absolute', left: left + w - 128, top: top + h + 8, width: BTN_W, height: BTN_H,
      }}>
        <Ripple left={BTN_W / 2} top={BTN_H / 2} keys={RIP_CREATE} color={TASKS.accent} size={30} />
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
          background: TASKS.accent, color: '#fff', borderRadius: 7,
          fontSize: 11, fontWeight: 600, boxShadow: '0 8px 18px -10px rgba(16,35,28,.55)',
        }}>
          <span style={{ fontSize: 13, lineHeight: 1 }}>＋</span>Создать задачу
        </div>
      </div>
    </>
  );
}

/**
 * Размер кнопки «Создать задачу» задан ЯВНО, а не выведен из текста: по её
 * центру наводится курсор (такт `path` в хореографии), и «нажал ровно в кнопку»
 * должно быть посчитано, а не угадано по ширине строки.
 */
const BTN_W = 116;
const BTN_H = 22;

/** Колонка на доске отчёта: заголовок и содержимое. */
function RepCol({
  left, top, w, title, children,
}: { left: number; top: number; w: number; title: string; children: React.ReactNode }) {
  return (
    <div style={{ position: 'absolute', left, top, width: w }}>
      <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.08em', color: MUTED, marginBottom: 8 }}>
        {title}
      </div>
      <div style={{ position: 'relative' }}>{children}</div>
    </div>
  );
}

/** Шаг работы агента на доске отчёта — карточка с номером. */
function RepStep({ cls, top, n, children }: { cls: string; top: number; n: number; children: React.ReactNode }) {
  return (
    <div className={cls} style={{
      position: 'absolute', left: 0, top, right: 0, display: 'flex', gap: 6, alignItems: 'flex-start',
      padding: '5px 7px', background: '#fff', border: `1px solid ${LINE}`, borderRadius: 6,
      fontSize: 10, lineHeight: 1.4, color: INK,
    }}>
      <span style={{
        width: 14, height: 14, borderRadius: '50%', flex: 'none', background: '#101613', color: '#e6efe9',
        fontSize: 8, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}>{n}</span>
      <span>{children}</span>
    </div>
  );
}

/**
 * Строка разбора артефакта: принят или отклонён.
 * Отклонённый ЗАЧЁРКНУТ и подписан причиной — «агент не взял» должно читаться
 * как решение с обоснованием, а не как пропуск.
 */
function RepVerdict({
  cls, top, ok, label, note,
}: { cls: string; top: number; ok: boolean; label: string; note: string }) {
  return (
    <div className={cls} style={{
      position: 'absolute', left: 0, top, right: 0, display: 'flex', gap: 6, alignItems: 'center',
      padding: '4px 7px', borderRadius: 6,
      background: ok ? 'rgba(61,165,92,.08)' : 'rgba(194,81,81,.07)',
      border: `1px solid ${ok ? 'rgba(61,165,92,.35)' : 'rgba(194,81,81,.35)'}`,
      fontSize: 9.5, lineHeight: 1.3,
    }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: ok ? '#2a7440' : '#a83f3f', flex: 'none' }}>
        {ok ? '✓' : '✕'}
      </span>
      <span style={{
        color: ok ? INK : '#8a8a8a', textDecoration: ok ? 'none' : 'line-through',
        minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{label}</span>
      <span style={{ marginLeft: 'auto', fontSize: 8.5, color: ok ? '#2a7440' : '#a83f3f', flex: 'none' }}>{note}</span>
    </div>
  );
}

/**
 * Карта пользовательских историй (USM) на доске отчёта.
 * Верхняя строка — шаги пользователя, ниже — истории. Новая история, которую
 * добавила эта задача, подсвечена: видно, что агент правит не только код.
 */
function RepUsm({ cls, newCls }: { cls: string; newCls: string }) {
  const steps = ['Собрал корзину', 'Вернулся', 'Оформил'];
  const rows: (string | null)[][] = [
    ['Добавить товар', 'Открыть корзину', 'Выбрать доставку'],
    ['Изменить количество', null, 'Оплатить'],
  ];
  return (
    <div className={cls} style={{ position: 'relative', height: 132 }}>
      {steps.map((s, i) => (
        <div key={s} style={{
          position: 'absolute', left: i * 104, top: 0, width: 96, padding: '4px 6px',
          background: TASKS.bg, border: `1px solid ${TASKS.light}`, borderRadius: 5,
          fontSize: 9, fontWeight: 600, color: TASKS.accent, textAlign: 'center',
        }}>{s}</div>
      ))}
      {rows.map((row, r) => row.map((cell, i) => cell && (
        <div key={`${r}-${i}`} style={{
          position: 'absolute', left: i * 104, top: 28 + r * 26, width: 96, padding: '3px 6px',
          background: '#fff', border: `1px solid ${LINE}`, borderRadius: 5,
          fontSize: 8.5, color: INK, textAlign: 'center',
        }}>{cell}</div>
      )))}
      {/* Новая история — та, ради которой всё и затевалось. */}
      <div className={newCls} style={{
        position: 'absolute', left: 104, top: 54, width: 96, padding: '3px 6px',
        background: 'rgba(61,165,92,.12)', border: '1px solid rgba(61,165,92,.5)', borderRadius: 5,
        fontSize: 8.5, fontWeight: 600, color: '#2a7440', textAlign: 'center',
      }}>Вернуться к корзине через 30 дней</div>
      <div style={{ position: 'absolute', left: 0, top: 112, fontSize: 8.5, color: MUTED }}>
        зелёным — что добавила задача
      </div>
    </div>
  );
}

/** Компактная строка артефакта в карточке задачи. */
function ArtRow({
  cls, top, label, icon, badge, rejected, glow,
}: {
  cls: string; top: number; label: string; icon: React.ReactNode;
  badge?: string; rejected?: boolean; glow?: string;
}) {
  return (
    <div className={cls} style={{
      ...(glow ? { ['--glow' as string]: glow } : null),
      position: 'absolute', left: 12, top, width: 300, height: 22,
      display: 'flex', alignItems: 'center', gap: 5, padding: '0 7px',
      background: rejected ? 'rgba(194,81,81,.06)' : '#fbfaf8',
      border: `1px solid ${rejected ? 'rgba(194,81,81,.3)' : LINE}`,
      borderRadius: 6, fontSize: 8.5, lineHeight: 1.2,
      color: rejected ? '#8a8a8a' : INK,
    }}>
      {icon}
      <span style={{
        minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        textDecoration: rejected ? 'line-through' : 'none',
      }}>{label}</span>
      {badge && (
        <span style={{
          marginLeft: 'auto', flex: 'none', fontSize: 7, fontWeight: 600, padding: '0 4px', borderRadius: 999,
          ...(rejected
            ? { color: '#a83f3f', background: 'rgba(194,81,81,.12)' }
            : { color: '#c06a3f', background: 'rgba(217,119,87,.14)' }),
        }}>{badge}</span>
      )}
    </div>
  );
}

/**
 * Пулл-реквест задачи — то, ЧЕМ работа кончилась в коде.
 *
 * Стоит отдельной панелью ВПЛОТНУЮ ПОД страницей задачи, а не строкой внутри
 * неё: в карточке места нет (её высота заморожена осколками финала), да и PR —
 * не ссылка в списке, а результат с разбором правок. Ветка, файлы и цифры
 * +/− говорят, что агент действительно менял код, а не отчитался словами.
 */
function PrCard() {
  const files: [string, string, string][] = [
    ['cart/ttl.service.ts', '+64', '−12'],
    ['cart/reserve.job.ts', '+31', '−8'],
    ['migrations/0042_cart_ttl.sql', '+19', '−0'],
    ['cart/ttl.service.spec.ts', '+88', '−0'],
  ];
  return (
    <div className="dm-pr" style={{
      position: 'absolute', left: PR_BOX.x, top: PR_BOX.y, width: PR_BOX.w, height: PR_BOX.h,
      background: CARD, border: `1px solid ${LINE}`, borderRadius: 12, overflow: 'hidden',
      boxShadow: '0 18px 38px -22px rgba(16,35,28,.45)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '7px 11px',
        background: '#fbfaf8', borderBottom: `1px solid ${LINE}`,
      }}>
        <svg width="11" height="11" viewBox="0 0 12 12" style={{ display: 'block', flex: 'none' }}>
          <circle cx="3" cy="3" r="1.8" fill="none" stroke="#2a7440" strokeWidth="1.3" />
          <circle cx="3" cy="9.2" r="1.8" fill="none" stroke="#2a7440" strokeWidth="1.3" />
          <circle cx="9.2" cy="9.2" r="1.8" fill="none" stroke="#2a7440" strokeWidth="1.3" />
          <path d="M3 4.8V7.4M4.8 3h2.6a1.8 1.8 0 0 1 1.8 1.8v2.6" fill="none" stroke="#2a7440" strokeWidth="1.3" />
        </svg>
        <span style={{ fontFamily: 'var(--pn-mono)', fontSize: 8.5, color: MUTED }}>PR #482</span>
        <span style={{
          marginLeft: 'auto', fontSize: 7.5, fontWeight: 600, color: '#2a7440',
          background: 'rgba(61,165,92,.12)', border: '1px solid rgba(61,165,92,.4)',
          borderRadius: 999, padding: '1px 7px',
        }}>смёржен</span>
      </div>
      <div className="dm-pr-head" style={{ padding: '8px 11px 0' }}>
        <div style={{ fontSize: 10.5, fontWeight: 600, color: INK, lineHeight: 1.25 }}>
          Хранить корзину 30 дней, снимать резерв на 7-й день
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 5 }}>
          <span style={{
            fontFamily: 'var(--pn-mono)', fontSize: 7.5, color: TASKS.accent,
            background: TASKS.bg, border: `1px solid ${TASKS.light}`, borderRadius: 4, padding: '1px 5px',
          }}>agent/SHOP-214</span>
          <span style={{ fontSize: 7.5, color: MUTED }}>→ main</span>
          <span style={{ marginLeft: 'auto', fontSize: 8, fontWeight: 600, color: '#2a7440' }}>+202</span>
          <span style={{ fontSize: 8, fontWeight: 600, color: '#a83f3f' }}>−20</span>
        </div>
      </div>
      <div style={{ padding: '7px 11px 0' }}>
        {files.map(([f, plus, minus], i) => (
          <div key={f} className={`dm-pr-f${i + 1}`} style={{
            display: 'flex', alignItems: 'center', gap: 6, height: 17,
            fontFamily: 'var(--pn-mono)', fontSize: 7.5, color: INK,
            borderTop: i ? `1px solid ${LINE}` : 'none',
          }}>
            <span style={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f}</span>
            <span style={{ color: '#2a7440', flex: 'none' }}>{plus}</span>
            <span style={{ color: '#a83f3f', flex: 'none' }}>{minus}</span>
          </div>
        ))}
      </div>
      <div className="dm-pr-foot" style={{
        position: 'absolute', left: 11, right: 11, bottom: 7,
        display: 'flex', alignItems: 'center', gap: 5, fontSize: 8, color: MUTED,
      }}>
        <Ava src="/claude-code-icon.svg" size={12} />
        <span style={{ color: INK, fontWeight: 500 }}>Claude Code</span>
        <span>· закрывает SHOP-214</span>
      </div>
    </div>
  );
}

/** Строка ленты задачи: кто и что сделал. */
function FeedRow({
  cls, top, who, what, src, color,
}: { cls: string; top: number; who: string; what: string; src?: string; color?: string }) {
  return (
    <div className={cls} style={{
      position: 'absolute', left: 12, top, display: 'flex', alignItems: 'center', gap: 5, fontSize: 8.5,
    }}>
      <Ava src={src} n={src ? undefined : who.slice(0, 2).toUpperCase()} color={color} size={12} />
      <span style={{ color: INK, fontWeight: 500 }}>{who}</span>
      <span style={{ color: MUTED }}>{what}</span>
    </div>
  );
}

/** Документ «Корзина: как устроено сейчас» — сквозной артефакт всей сцены. */
const ARTIFACT = 'Корзина: как устроено сейчас';

/* ── Сеть MCP ──────────────────────────────────────────────────────────────
   Зелень MCP — та же, что у линии к агенту: сеть и канал связи — одно и то же.
   Шина идёт по зазору ПОД окнами (728), ниже страницы задачи (кончается на 608)
   и ниже доски (700), но внутри рамки (кончается на 760). Прямые нити от
   шильдика к разделам пришлось бы вести сквозь окно задачи — трасса с шиной и
   отводами и честнее, и читается как сеть, а не как пучок лучей. */
/**
 * Пулл-реквест стоит ВПЛОТНУЮ ПОД страницей задачи и по её левой кромке: он
 * принадлежит задаче, просто не помещается внутрь карточки. Появляется на месте,
 * освободившемся после того, как доска хода работы схлопнулась в задачу.
 */
const PR_BOX = { x: 1040, y: 624, w: 340, h: 196 } as const;

const NET_INK = '#63c69e';
const NET_BUS_Y = 728;
/** Радиус скругления углов трассы. */
const NET_R = 24;

type NetTap = {
  rail: string; pack: string; dot: string;
  /** X отвода на шине и Y, до которого он поднимается. */
  x: number; y: number;
  /** Доводка вбок после подъёма — нужна ярлыку Базы знаний на кромке доски. */
  to?: number;
};

const NET_TAPS: NetTap[] = [
  // Страница задачи — снизу, в её нижнюю кромку.
  { rail: 'dm-net-r1', pack: 'dm-net-p1', dot: 'dm-net-e1', x: 1210, y: 604 },
  // Ярлык Базы знаний на правой кромке доски: подъём в зазоре между доской и
  // страницей задачи (1000…1040), затем доводка влево к самому корешку.
  { rail: 'dm-net-r2', pack: 'dm-net-p2', dot: 'dm-net-e2', x: 1020, y: 452, to: 998 },
  // Кнопка чата в правом нижнем углу канвы.
  { rail: 'dm-net-r3', pack: 'dm-net-p3', dot: 'dm-net-e3', x: 963, y: 680 },
  // Сама доска — отвод далеко влево, чтобы сеть охватывала пространство,
  // а не жалась к одному его краю.
  { rail: 'dm-net-r4', pack: 'dm-net-p4', dot: 'dm-net-e4', x: 430, y: 696 },
];

/** Трасса отвода: спуск от шильдика, прогон по шине, подъём в раздел. */
function netPath(tap: NetTap): string {
  const gx = MCP_GATE.x;
  const gy = MCP_GATE.y + MCP_GATE.h / 2;
  return `M ${gx} ${gy} L ${gx} ${NET_BUS_Y - NET_R}`
    + ` Q ${gx} ${NET_BUS_Y} ${gx - NET_R} ${NET_BUS_Y}`
    + ` L ${tap.x + NET_R} ${NET_BUS_Y}`
    + ` Q ${tap.x} ${NET_BUS_Y} ${tap.x} ${NET_BUS_Y - NET_R}`
    + ` L ${tap.x} ${tap.y}`
    + (tap.to !== undefined ? ` L ${tap.to} ${tap.y}` : '');
}

/**
 * Кейфреймы лоадера доски — ПЕРЕНЕСЕНЫ ИЗ ПРОДУКТА как есть
 * (BoardLoadingOverlay, initiation/board): та же плитка со стикерами, тот же
 * «поток» и те же точки. Смысл в узнавании: пользователь увидит ровно этот
 * экран, когда откроет доску, — сцена не выдумывает свою заставку.
 *
 * Тайминги оставлены авторские (3.4 с поток, 10.2 с перекраска): их фаза не
 * привязана к циклу сцены, но лоадер живёт полторы секунды — за это время
 * рассинхрон не читается.
 */
const LOADER_CSS = `
@keyframes dm-loader-flow {
  0%   { transform: translateX(-46px) scale(.78); opacity: 0; }
  17%  { transform: translateX(0) scale(1.08);    opacity: 1; }
  24%  { transform: translateX(0) scale(1);       opacity: 1; }
  74%  { transform: translateX(0) scale(1);       opacity: 1; }
  100% { transform: translateX(46px) scale(.78);  opacity: 0; }
}
@keyframes dm-loader-dots { 0%,80%,100% { opacity:.25 } 40% { opacity:1 } }
`;

/** Плитка лоадера: 2×2 стикера с «потоком», точки и подпись. */
function BoardLoader() {
  const tile = (bg: string, delay: number, lines: [string, string]) => (
    <div style={{
      borderRadius: 8, background: bg, padding: 7,
      boxShadow: '0 4px 9px rgba(45,40,30,0.12), inset 0 1px 0 rgba(255,255,255,0.6)',
      animation: 'dm-loader-flow 3.4s ease-in-out infinite', animationDelay: `${delay}ms`,
    }}>
      <div style={{ height: 4, width: lines[0], borderRadius: 2, background: '#2f7a55', opacity: 0.5 }} />
      <div style={{ height: 4, width: lines[1], borderRadius: 2, background: '#2f7a55', opacity: 0.35, marginTop: 5 }} />
    </div>
  );
  return (
    <div className="dm-board-loader" style={{
      position: 'absolute', inset: 0, zIndex: 20,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 26,
      background: 'rgba(255,255,255,0.55)',
    }}>
      <div style={{ position: 'relative', width: 132, height: 132 }}>
        <div style={{
          position: 'absolute', inset: 0, borderRadius: 26,
          background: 'rgba(255,255,255,0.62)', border: '1px solid rgba(255,255,255,0.7)',
          boxShadow: '0 18px 40px -14px rgba(40,35,25,0.28), inset 0 1px 0 rgba(255,255,255,0.7)',
        }} />
        <div style={{
          position: 'absolute', inset: 22, display: 'grid',
          gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: 12,
        }}>
          {tile('#a8ddc0', 0, ['70%', '48%'])}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'dm-loader-flow 3.4s ease-in-out infinite', animationDelay: '100ms',
          }}>
            <svg width="100%" height="100%" viewBox="0 0 44 44" style={{ filter: 'drop-shadow(0 4px 9px rgba(45,40,30,0.12))' }}>
              <circle cx="22" cy="22" r="17" fill="#fbe487" />
            </svg>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'dm-loader-flow 3.4s ease-in-out infinite', animationDelay: '200ms',
          }}>
            <svg width="100%" height="100%" viewBox="0 0 44 44" style={{ filter: 'drop-shadow(0 4px 9px rgba(45,40,30,0.12))' }}>
              <path d="M22 6 L38 36 L6 36 Z" fill="#a6d6e2" strokeLinejoin="round" />
            </svg>
          </div>
          {tile('#c7bcef', 300, ['58%', '46%'])}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 11 }}>
        <div style={{ display: 'flex', gap: 7 }}>
          {['#51a880', '#b08a3e', '#5454a1'].map((col, i) => (
            <span key={col} style={{
              width: 8, height: 8, borderRadius: '50%', background: col,
              animation: 'dm-loader-dots 1.2s ease-in-out infinite', animationDelay: `${i * 150}ms`,
            }} />
          ))}
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#6a655c', letterSpacing: '0.01em' }}>
          Загружаем доску…
        </div>
      </div>
    </div>
  );
}

/**
 * CSS хореографии считается ОДИН РАЗ на модуль: таблицы тактов и кадров
 * статичны, а строка большая — пересобирать её на каждый рендер незачем.
 */
const TIMELINE_CSS = buildTimelineCss();

/**
 * `animated` включает хореографию: в сцену вставляется <style> с кейфреймами,
 * а элементы ловят их по классам. Без флага сцена статична и показывает всё
 * сразу — в таком виде её удобно проверять на стенде.
 */
export function DemoScene({ animated = false }: { animated?: boolean } = {}): React.ReactElement {
  const body = (
    <>
      {/* ── Рамка «одно окно» ─────────────────────────────────────────────── */}
      <div className="dm-frame dm-frame-box" style={{
        position: 'absolute', left: FRAME.x, top: FRAME.y, width: FRAME.w, height: FRAME.h,
        border: '1.5px solid rgba(18,36,29,.22)', borderRadius: 22,
        background: 'rgba(255,255,255,.35)',
      }} />
      <div className="dm-frame" style={{
        position: 'absolute', left: FRAME.x + 26, top: FRAME.y - 11, padding: '2px 12px',
        background: '#f4f2ee', border: '1px solid rgba(18,36,29,.18)', borderRadius: 999,
        fontSize: 11, fontWeight: 700, letterSpacing: '.12em', color: INK,
      }}>
        PRONA
      </div>

      {/* ══ ДОСКА КОМАНДЫ ══════════════════════════════════════════════════
          Начинает ПУСТОЙ. Всё, что на ней появится, приносят участники по ходу
          сцены: сначала формулировка проблемы, потом данные, потом документ из
          Базы знаний. Пустая канва в начале — не экономия, а условие: иначе не
          видно, что задача рождается из обсуждения, а не лежала готовой. */}
      <Window zone={ZONES.board} cls="dm-w-board">
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'radial-gradient(rgba(18,36,29,.13) 1.2px, transparent 1.4px)',
          backgroundSize: '22px 22px',
        }} />

        <BoardLoader />

        <div className="dm-bm-title" style={{
          position: 'absolute', left: 20, top: 12, fontSize: 14, fontWeight: 600, color: INK,
        }}>
          Запрос на увеличение времени хранения корзины с 24 ч до 30 дней
          <span style={{ fontSize: 10, color: MUTED, fontWeight: 400, marginLeft: 8 }}>тема · Аня</span>
        </div>

        <Sticky cls="dm-bm-s1" left={20} top={40} w={196} h={62} tone="gold">
          Корзина живёт 24 часа — собранный заказ теряется
        </Sticky>
        <Sticky cls="dm-bm-s2" left={228} top={40} w={196} h={62} tone="indigo">
          Клиент просит хранить 30 дней
        </Sticky>
        <Sticky cls="dm-bm-s3" left={436} top={40} w={196} h={62} tone="green">
          Влияет на резерв остатков
        </Sticky>

        <div className="dm-bm-tbl"><MiniTable left={20} top={116} w={200} /></div>
        <MiniScheme left={244} top={116} />

        <BoardDocCard cls="dm-bm-doc" glow={ART} left={256} top={230} w={320} h={124} />
        {/*
          Вопрос НАБИРАЕТ Аня — на обычном стикере доски, не на карточке-плашке.
          Текст печатается построчно: реветь `clip-path` шторкой со ступенями по
          числу знаков (такт kind:'type'). Раньше анимировалась ШИРИНА до
          заданного числа пикселей, и стоило ошибиться в оценке — хвост строки
          обрезался насовсем. Клип этого не требует: строка живёт своей
          естественной шириной.
        */}
        <Sticky cls="dm-bm-note" left={20} top={242} w={218} h={118} tone="gold">
          <span className="dm-note-l1" style={{ display: 'block', whiteSpace: 'nowrap' }}>
            Откуда 30 дней?
          </span>
          <span className="dm-note-l2" style={{ display: 'block', whiteSpace: 'nowrap' }}>
            Кто просил и когда —
          </span>
          <span className="dm-note-l3" style={{ display: 'block', whiteSpace: 'nowrap' }}>
            не помним
          </span>
        </Sticky>

        {/*
          Чат вызывается КНОПКОЙ В ПРАВОМ НИЖНЕМ УГЛУ КАНВЫ и открывается
          вертикальной панелью поверх доски. Кнопка висит всё время, пока живёт
          доска: это постоянная часть рабочего полотна, а не приглашение,
          которое выскакивает под сценарий. Агент к ней подъезжает и нажимает —
          нажатие видно по расходящимся кольцам.
          У Базы знаний иначе: там раздел открывается ПАНЕЛЬЮ СБОКУ, и корешок
          стоит на той кромке, за которой панель и появится.
        */}
        <div className="dm-invite-chat" style={{
          ['--glow' as string]: CHAT.accent,
          position: 'absolute', right: 16, bottom: 16, width: 42, height: 42, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: '#fff', border: `1.4px solid ${CHAT.accent}`,
          boxShadow: '0 10px 22px -12px rgba(16,35,28,.6)',
        }}>
          <Ripple left={21} top={21} keys={RIP_CHAT} color={CHAT.accent} size={34} />
          <ChatGlyph color={CHAT.accent} size={19} />
          {/* Непрочитанное — знак, что в переписке есть чего искать. */}
          <span style={{
            position: 'absolute', right: 1, top: 1, minWidth: 14, height: 14, borderRadius: 999,
            background: '#c25151', color: '#fff', fontSize: 8, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px',
          }}>2</span>
        </div>

        {/*
          Приглашение на КРОМКЕ доски — вертикальный ярлык-корешок, как закладка
          у края страницы. Горизонтальная плашка занимала полосу канвы и читалась
          как элемент доски; корешок явно принадлежит границе окна и говорит:
          раздел здесь, за краем, открыть — один клик.
        */}
        <div className="dm-invite-kb" style={{
          ['--glow' as string]: KNOW.accent,
          position: 'absolute', right: 0, top: 236, width: 30, height: 132,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
          padding: '9px 0', background: '#fff',
          border: `1px solid ${KNOW.muted}`, borderRight: 'none',
          borderRadius: '9px 0 0 9px', boxShadow: '-6px 0 16px -12px rgba(16,35,28,.55)',
        }}>
          {/* eslint-disable-next-line @next/next/no-img-element -- сцена, не контентная картинка */}
          <img src="/prona-knowledge-section-chrome.svg" alt="" style={{ width: 16, height: 16, borderRadius: '50%', display: 'block', flex: 'none' }} />
          <span style={{
            writingMode: 'vertical-rl', transform: 'rotate(180deg)',
            fontSize: 10, fontWeight: 600, color: KNOW.accent, letterSpacing: '.02em', whiteSpace: 'nowrap',
          }}>
            База знаний
          </span>
        </div>

        {/* Ответ агента на вопрос — обычным стикером доски. Он не докладывает
            в чат и не пишет в сторону: находка ложится туда же, где задан
            вопрос, и попадает в выделение вместе со всем остальным. */}
        {/* Ответ ложится ПОВЕРХ вопроса, со сдвигом вниз-вправо: жёлтый край
            вопроса остаётся виден, и связь «на этот вопрос — этот ответ»
            читается без стрелок и подписей. Порядок в разметке важен: ответ
            идёт после вопроса, поэтому и рисуется поверх. */}
        <Sticky cls="dm-bm-answer" left={36} top={254} w={212} h={104} tone="green">
          <span style={{ display: 'block', fontSize: 9.5, fontWeight: 700, letterSpacing: '.05em', color: '#2f6b4c', marginBottom: 4 }}>
            НАШЁЛ В ЧАТЕ
          </span>
          {/* Ответ агент тоже НАБИРАЕТ — как человек писал вопрос. Одинаковая
              механика у обоих: видно, что это реплики в одном разговоре, а не
              «человек пишет, система выдаёт». Строки однострочные — того
              требует побуквенный набор. */}
          <span className="dm-ans-l1" style={{ display: 'block', whiteSpace: 'nowrap' }}>
            Клиент просил 30 дней —
          </span>
          <span className="dm-ans-l2" style={{ display: 'block', whiteSpace: 'nowrap' }}>
            запрос от 12 февраля
          </span>
        </Sticky>

        <Lasso cls="dm-lasso" btnCls="dm-lasso-btn" left={10} top={30} w={850} h={334} />

        {/* Карточка стоит у ПРАВОГО края доски: именно этот край показывает кадр
            «доска + панель», и связка «один объект» должна быть видна целиком.
            Правее неё оставлен зазор под кнопку чата — та висит в самом углу. */}
        <BoardTaskCard cls="dm-b-task" glow={TASKS.accent} left={496} top={396} w={320} h={124} />

      </Window>

      {/* ══ МИРОВОЙ СЛОЙ: курсоры и перетаскиваемое ═══════════════════════
          Живут ВНЕ окон, поверх всего. Иначе `overflow: hidden` окна режет
          курсор на кромке, и Лена физически не может вынести документ из
          соседней панели на доску — а именно это движение и надо показать.
          Координаты — мировые: начало контента доски (120, 150). */}

      {/* Документ, который Лена тянет из панели Базы знаний на доску. */}
      <div className="dm-drag-doc" style={{
        position: 'absolute', left: 376, top: 380, width: 320, height: 124, zIndex: 35,
        background: '#fff', border: `1.4px solid ${ART}`, borderRadius: 8,
        boxShadow: '0 18px 34px -16px rgba(16,35,28,.6)',
        display: 'flex', alignItems: 'center', gap: 8, padding: '0 14px',
        fontSize: 12, fontWeight: 600, color: ART, pointerEvents: 'none',
      }}>
        <DocGlyph color={ART} size={12} />{ARTIFACT}
      </div>

      {/* У Ани и Игоря реплик нет: их вклад виден на самой доске — таблица,
          схема и напечатанный вопрос. Пузырь поверх этого дублировал бы то, что
          зритель и так читает. Реплика осталась у агента: его уход из кадра
          иначе ничем не объяснить. */}
      <Cursor cls="dm-c-anya" x={190} y={290} name="Аня" color="#51a880" />
      <Cursor cls="dm-c-igor" x={590} y={250} name="Игорь" color="#5454a1" />
      {/*
        Третий на доске — СВОЙ агент платформы: он приносит документацию из Базы
        знаний и работает наравне с людьми. Внешний Claude Code появится позже и
        снаружи, через MCP: это разные роли, и путать их нельзя.
        Цвет — shell-бирюза из дизайн-системы, «общий слой платформы»: не спорит
        ни с зелёным Ани, ни с индиго Игоря.
      */}
      <Cursor cls="dm-c-lena" x={240} y={430} name="Агент PRONA" color="#4e8992" agent z={50}
              icon="/prona-logo-chrome.svg" says={[
        { cls: 'dm-say-l1', text: 'Поищу документацию в Базе знаний', w: 240 },
      ]} />

      {/* ══ ЗАДАЧА ═════════════════════════════════════════════════════════
          Появляется НЕ СРАЗУ: окно возникает в тот же миг, когда на доске
          нажали «Создать задачу», и сразу со связанными артефактами — ровно
          теми, что были в выделении. */}
      <Window zone={ZONES.task} cls="dm-w-task">
        <div className="dm-t-head" style={{ ['--glow' as string]: TASKS.accent, position: 'absolute', left: 8, top: 6, right: 8, height: 44, borderRadius: 6 }}>
          <div style={{ position: 'absolute', left: 4, top: 3, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              fontFamily: 'var(--pn-mono)', fontSize: 8.5, color: TASKS.accent,
              background: TASKS.bg, border: `1px solid ${TASKS.light}`, borderRadius: 4, padding: '1px 5px',
            }}>SHOP-214</span>
            <span style={{ fontSize: 8, color: MUTED }}>создана с доски · Аня</span>
          </div>
          <div style={{ position: 'absolute', left: 4, top: 22, width: 300, fontSize: 12.5, fontWeight: 600, color: INK, lineHeight: 1.25 }}>
            Хранить корзину 30 дней
          </div>
        </div>
        <div className="dm-t-desc">
          <Bar w={280} top={56} />
          <Bar w={218} top={68} />
        </div>

        <div className="dm-t-fields">
          <Field top={88} label="Исполнитель" cls="dm-t-asg-field" glow={TASKS.accent}>
            <span className="dm-t-asg-empty" style={{ position: 'absolute', left: 7, right: 7, top: 0, bottom: 0, display: 'flex', alignItems: 'center', color: MUTED }}>
              Назначить исполнителя
            </span>
            <span className="dm-t-asg-agent" style={{ position: 'absolute', left: 7, right: 7, top: 0, bottom: 0, display: 'flex', alignItems: 'center', gap: 5 }}>
              <Ripple left={7} top={10} keys={RIP_ASSIGN} />
              <Ava src="/claude-code-icon.svg" size={13} />
              <span style={{ color: INK, fontWeight: 500 }}>Claude Code</span>
              <span style={{ marginLeft: 'auto', fontSize: 7.5, color: KNOW.accent, background: KNOW.bg, border: `1px solid ${KNOW.muted}`, borderRadius: 999, padding: '0 5px' }}>агент</span>
            </span>
          </Field>
          <Field top={112} label="Автор">
            <Ava n="АН" color="#51a880" size={13} />
            <span style={{ color: INK }}>Аня Носова</span>
          </Field>
          {/*
            Пилюли статуса ЦЕНТРИРУЮТСЯ ФЛЕКСОМ, а не отступом сверху: при
            `top: 4` пилюля высотой 16.6 в поле высотой 20 свисала на полпикселя
            ниже кромки и стояла ниже, чем содержимое соседних полей. Центровать
            через `transform: translateY(-50%)` нельзя — такт появления
            (kind:'pop') сам правит transform и снёс бы центровку.
          */}
          <Field top={136} label="Статус">
            {([
              ['dm-t-st-backlog', 'Бэклог', ROW.meta, '#f2f3f5', ROW.border],
              ['dm-t-st-work', 'В работе', TASKS.accent, TASKS.bg, TASKS.light],
              ['dm-t-st-done', 'Готово', '#2a7440', 'rgba(61,165,92,.12)', 'rgba(61,165,92,.4)'],
            ] as const).map(([cls, label, color, bg, bd]) => (
              <span key={cls} className={cls} style={{
                position: 'absolute', left: 7, top: 0, bottom: 0, display: 'flex', alignItems: 'center',
              }}>
                <span style={{
                  fontSize: 8, fontWeight: 600, color, background: bg,
                  border: `1px solid ${bd}`, borderRadius: 999, padding: '1px 7px',
                }}>{label}</span>
              </span>
            ))}
          </Field>
          <Field top={160} label="Приоритет">
            <Prio level="high" />
            <span style={{ color: INK }}>Высокий</span>
            <span style={{ marginLeft: 'auto', color: MUTED, fontSize: 8 }}>SP 5</span>
          </Field>
        </div>

        {/*
          Список исполнителей — он и делает назначение действием, а не фактом:
          видно, что человек открыл поле, выбрал агента из тех же людей и
          подтвердил. Лежит поверх полей, поэтому в потоке его нет.
        */}
        <div className="dm-t-asg-list" style={{
          position: 'absolute', left: 86, top: 110, width: 238, zIndex: 6,
          background: '#fff', border: `1px solid ${LINE}`, borderRadius: 7,
          boxShadow: '0 14px 28px -14px rgba(16,35,28,.5)', overflow: 'hidden',
        }}>
          {[
            { n: 'Аня Носова', c: '#51a880', i: 'АН' },
            { n: 'Игорь Кравцов', c: '#5454a1', i: 'ИК' },
            { n: 'Агент PRONA', c: '', i: '', icon: '/prona-logo-chrome.svg' },
            { n: 'Claude Code', c: '', i: '', agent: true },
          ].map((o) => (
            <div key={o.n} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', fontSize: 9,
              background: o.agent ? TASKS.bg : '#fff', color: INK,
              fontWeight: o.agent ? 600 : 400,
            }}>
              {o.agent
                ? <Ava src="/claude-code-icon.svg" size={13} />
                : <Ava src={o.icon} n={o.icon ? undefined : o.i} color={o.c} size={13} />}
              {o.n}
              {o.agent && (
                <span style={{
                  marginLeft: 'auto', fontSize: 7.5, color: KNOW.accent, background: KNOW.bg,
                  border: `1px solid ${KNOW.muted}`, borderRadius: 999, padding: '0 5px',
                }}>агент</span>
              )}
            </div>
          ))}
        </div>

        {/*
          Шаг строк артефактов 24, а не 26: седьмая строка — доска хода работы,
          которая в конце сцены схлопывается сюда, — иначе не помещалась в
          карточку. Высота карточки менять НЕЛЬЗЯ: к ней привязаны осколки финала
          (OUTRO_PARTS). При любом сдвиге этих строк таблицу осколков надо
          сдвигать на ту же дельту — координаты там абсолютные.
        */}
        <div className="dm-t-arts" style={{ position: 'absolute', left: 12, top: 186, fontSize: 8, letterSpacing: '.08em', color: MUTED, fontWeight: 600 }}>
          СВЯЗАННЫЕ АРТЕФАКТЫ
        </div>
        {/* Три первых пришли из выделения на доске… */}
        <ArtRow cls="dm-t-a1" glow={ART} top={199} label={ARTIFACT} icon={<SourceMark src={SRC_MARK.know} />} />
        <ArtRow cls="dm-t-a2" top={223} label="Доска · Долгое хранение корзины" icon={<SourceMark src={SRC_MARK.board} />} />
        <ArtRow cls="dm-t-a3" top={247} label="Данные · срок жизни корзин" icon={<SourceMark src={SRC_MARK.board} />} />
        {/* …две следующих нашёл и приложил агент… */}
        <ArtRow cls="dm-t-a4" top={271} label="Запрос клиента · 6 мес назад" icon={<SourceMark src={SRC_MARK.chat} />} badge="нашёл агент" />
        <ArtRow cls="dm-t-a5" top={295} label="Поддержка · 23 обращения" icon={<SourceMark src={SRC_MARK.chat} />} badge="нашёл агент" />
        {/* …этот нашёл и НЕ приложил, с причиной… */}
        <ArtRow cls="dm-t-a6" top={319} label="Регламент хранения v1" icon={<SourceMark src={SRC_MARK.know} />} badge="устарел" rejected />
        {/* …а это — доска, которую агент завёл сам и схлопнул сюда вложением. */}
        <ArtRow cls="dm-t-a7" glow={TASKS.accent} top={343} label="Ход работы агента · доска" icon={<SourceMark src={SRC_MARK.board} />} badge="завёл агент" />

        <div className="dm-t-feed" style={{ position: 'absolute', left: 12, top: 369, fontSize: 8, letterSpacing: '.08em', color: MUTED, fontWeight: 600 }}>
          ЛЕНТА
        </div>
        <FeedRow cls="dm-t-f1" top={382} who="Аня" what="создала задачу с доски" color="#51a880" />
        <FeedRow cls="dm-t-f2" top={398} who="Claude Code" what="взял в работу" src="/claude-code-icon.svg" />
        <FeedRow cls="dm-t-f3" top={414} who="Claude Code" what="приложил 2 артефакта, 1 отклонил" src="/claude-code-icon.svg" />
      </Window>

      {/* ══ БЭКЛОГ ═════════════════════════════════════════════════════════ */}
      <Window zone={ZONES.backlog} cls="dm-w-backlog">
        <SectionHead cls="dm-bl-head" top={8} name="Спринт 24" goal="долгое хранение корзины" active
                     counts={{ a: '5 задач', b: '6 задач', aCls: 'dm-bl-cnt5', bCls: 'dm-bl-cnt6' }} />
        <BacklogRow cls="dm-bl-row214" glow={TASKS.accent} avaCls="dm-bl-ava"
                    top={30} k="SHOP-214" title="Хранить корзину 30 дней" prio="high" sp="5"
                    ava={{ src: '/claude-code-icon.svg' }} checked />
        {/* Всё, что ниже вставленной строки, живёт одним слоем: когда задача
            попадает в спринт, слой съезжает вниз на высоту строки — это и есть
            «список раздвинулся», а не «строка проявилась в пустом месте». */}
        <div className="dm-bl-rows" style={{ position: 'absolute', inset: 0 }}>
          <BacklogRow top={58} k="SHOP-215" title="Миграция схемы корзины" prio="medium" sp="3" ava={{ n: 'ИК', color: '#5454a1' }} />
          <BacklogRow top={86} k="SHOP-216" title="Снятие резерва остатков по TTL" prio="medium" sp="2" ava={{ n: 'АС', color: '#51a880' }} />
          <BacklogRow top={114} k="SHOP-217" title="Письмо о скором сгорании корзины" prio="low" sp="2" ava={{ n: 'ЛМ', color: '#b08a3e' }} />
          <BacklogRow top={142} k="SHOP-219" title="Аналитика: срок жизни корзин" prio="low" sp="1" ava={{ n: 'ИК', color: '#5454a1' }} />

          <SectionHead top={178} name="Спринт 25" goal="оплата и доставка" count="4 задачи" />
          <BacklogRow top={200} k="SHOP-221" title="Сплит-оплата заказа" prio="medium" sp="8" ava={{ n: 'АС', color: '#51a880' }} dim />
          <BacklogRow top={228} k="SHOP-224" title="Выбор пункта выдачи на карте" prio="medium" sp="5" ava={{ n: 'ЛМ', color: '#b08a3e' }} dim />
          <BacklogRow top={256} k="SHOP-228" title="Повторить прошлый заказ" prio="low" sp="3" ava={{ n: 'ИК', color: '#5454a1' }} dim />

          <SectionHead top={294} name="Бэклог" count="12 задач" />
          <BacklogRow top={316} k="SHOP-231" title="Восстановление корзины после входа" prio="medium" sp="—" ava={{}} dim />
          <BacklogRow top={344} k="SHOP-233" title="Лимит позиций в корзине" prio="low" sp="3" ava={{ n: 'АС', color: '#51a880' }} dim />
          <BacklogRow top={372} k="SHOP-240" title="Выгрузка брошенных корзин" prio="low" sp="—" ava={{}} dim />
          <div style={{ position: 'absolute', left: 17, top: 406, fontSize: 9, color: TASKS.accent }}>+ Создать задачу</div>
        </div>
      </Window>

      {/* ══ ЧАТ ════════════════════════════════════════════════════════════
          ПРАВЫЙ САЙДБАР ДОСКИ — выезжает вдоль кромки по кнопке в правом нижнем
          углу канвы. Сначала показывает обычную жизнь команды, ничего про
          корзину. Историческую переписку полугодовой давности вытаскивает агент,
          когда ищет контекст: до этого её на экране нет, и находка читается
          как находка.
          Раскладка СТОЛБИКОМ: панель узкая, реплики идут сверху вниз, чужие
          слева, свои справа — как в живом чате, а не в две колонки. */}
      <Window zone={ZONES.chat} cls="dm-w-chat" dock z={45}>
        <div className="dm-ch-now">
          <div style={{ position: 'absolute', left: 0, right: 0, top: 8, display: 'flex', justifyContent: 'center' }}>
            <span style={{
              fontSize: 9, color: '#4a5260', background: '#eef0f4', border: '1px solid #dde0e6',
              borderRadius: 999, padding: '2px 10px', fontWeight: 600,
            }}>сегодня</span>
          </div>
          <div style={{ position: 'absolute', left: 14, top: 32, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Ava n="ИК" color={CHAT.accent} size={15} />
            <span style={{ fontSize: 10, fontWeight: 600, color: CHAT.accent }}>Игорь Кравцов</span>
            <span style={{ fontSize: 9, color: MUTED }}>09:12</span>
          </div>
          <div style={{
            position: 'absolute', left: 34, top: 52, width: 250, padding: '7px 10px',
            background: '#fff', border: `1px solid ${LINE}`, borderRadius: 9, borderBottomLeftRadius: 3,
            fontSize: 10.5, lineHeight: 1.45, color: INK,
          }}>
            Релиз 4.2 выкатили, метрики ровные. Сегодня разбираем бэклог спринта.
          </div>
          <div style={{
            position: 'absolute', right: 14, top: 126, width: 220, padding: '7px 10px',
            background: CHAT.selfBg, border: `1px solid ${CHAT.selfBorder}`, borderRadius: 9, borderBottomRightRadius: 3,
            fontSize: 10.5, lineHeight: 1.45, color: '#1f2530',
          }}>
            Ок, буду на доске через 10 минут
            <span style={{ display: 'block', textAlign: 'right', fontSize: 8.5, color: MUTED, marginTop: 3 }}>09:15</span>
          </div>
        </div>

        {/* Поисковый запрос агента — видно, ЧТО он ищет, а не просто «нашёл». */}
        <div className="dm-ch-search" style={{
          position: 'absolute', left: 12, right: 12, top: 8, height: 24,
          display: 'flex', alignItems: 'center', gap: 6, padding: '0 9px',
          background: '#fff', border: `1px solid ${TASKS.light}`, borderRadius: 7, fontSize: 9.5,
        }}>
          <span style={{ color: MUTED }}>⌕</span>
          <span style={{ color: INK, whiteSpace: 'nowrap' }}>корзина 30 дней · хранение</span>
          <span style={{ marginLeft: 'auto', color: TASKS.accent, fontWeight: 600, whiteSpace: 'nowrap' }}>2</span>
        </div>

        <div className="dm-ch-old">
          <div style={{ position: 'absolute', left: 0, right: 0, top: 42, display: 'flex', justifyContent: 'center' }}>
            <span style={{
              fontSize: 9, color: '#4a5260', background: '#eef0f4', border: '1px solid #dde0e6',
              borderRadius: 999, padding: '2px 10px', fontWeight: 600,
            }}>12 февраля · полгода назад</span>
          </div>
          <div style={{ position: 'absolute', left: 14, top: 68, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Ava n="МК" color={CHAT.accent} size={15} />
            <span style={{ fontSize: 10, fontWeight: 600, color: CHAT.accent }}>Марина Котова</span>
            <span style={{ fontSize: 9, color: MUTED }}>клиент</span>
          </div>
          <div className="dm-ch-msg" style={{
            ['--glow' as string]: CHAT.accent,
            position: 'absolute', left: 34, top: 88, width: 284, padding: '7px 10px',
            background: '#fff', border: `1px solid ${LINE}`, borderRadius: 9, borderBottomLeftRadius: 3,
            fontSize: 10.5, lineHeight: 1.45, color: INK,
          }}>
            Корзина очищается за сутки — клиенты собирают заказ и теряют его. Можно 30 дней?
          </div>
          <div className="dm-ch-att" style={{
            ['--glow' as string]: ART,
            position: 'absolute', left: 34, top: 172, width: 284, padding: '5px 10px',
            display: 'flex', alignItems: 'center', gap: 7,
            background: ART_BG, border: `1px solid ${ART_BD}`,
            borderRadius: 7, fontSize: 9.5, color: ART, fontWeight: 600,
          }}>
            <DocGlyph color={ART} size={10} />{ARTIFACT}
          </div>
          <div style={{
            position: 'absolute', right: 14, top: 216, width: 220, padding: '7px 10px',
            background: CHAT.selfBg, border: `1px solid ${CHAT.selfBorder}`, borderRadius: 9, borderBottomRightRadius: 3,
            fontSize: 10.5, lineHeight: 1.45, color: '#1f2530',
          }}>
            Приняли, заведём тему на разбор
            <span style={{ display: 'block', textAlign: 'right', fontSize: 8.5, color: MUTED, marginTop: 3 }}>12:04</span>
          </div>
        </div>
      </Window>

      {/* ══ БАЗА ЗНАНИЙ ════════════════════════════════════════════════════
          Тоже начинает со своей обычной жизни: открыт регламент оформления
          заказа, к теме сцены отношения не имеющий. Документ про корзину
          открывает Лена, когда ищет описание процесса. */}
      <Window zone={ZONES.kb} cls="dm-w-kb">
        <div className="dm-kb-tree" style={{
          position: 'absolute', left: 0, top: 0, bottom: 0, width: 106,
          background: '#faf9f7', borderRight: `1px solid ${DOC.line}`, padding: '10px 8px',
        }}>
          <TreeRow indent={0} label="Процессы" folder open />
          <TreeRow indent={1} label="Оформление заказа" />
          <div className="dm-kb-row-cart" style={{ ['--glow' as string]: ART }}>
            <TreeRow indent={1} label="Корзина" active />
          </div>
          <TreeRow indent={1} label="Резерв остатков" />
          <TreeRow indent={0} label="Регламенты" folder />
        </div>

        <div className="dm-kb-pageA" style={{
          position: 'absolute', left: 106, right: 0, top: 0, bottom: 0, padding: '12px 14px',
          fontFamily: 'var(--pn-doc)', color: DOC.ink,
        }}>
          <div style={{ fontSize: 7.5, color: DOC.mut }}>Процессы / Оформление заказа</div>
          <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2, letterSpacing: '-.02em', margin: '4px 0 7px' }}>
            Оформление заказа
          </div>
          <p style={{ fontSize: 8, lineHeight: 1.62, margin: '0 0 8px' }}>
            Шаги оформления, проверка адреса и способов оплаты. Регламент действует
            для розничных заказов с 2024 года.
          </p>
          <div style={{ fontSize: 9, fontWeight: 600, margin: '0 0 4px' }}>Кто отвечает</div>
          {['Продуктовая команда «Заказ»', 'Поддержка первой линии', 'Логистика — за сроки доставки'].map((t) => (
            <div key={t} style={{ display: 'flex', gap: 5, fontSize: 8, lineHeight: 1.55 }}>
              <span style={{ color: DOC.mut }}>•</span><span>{t}</span>
            </div>
          ))}
        </div>

        <div className="dm-kb-pageB" style={{
          ['--glow' as string]: ART,
          position: 'absolute', left: 106, right: 0, top: 0, bottom: 0, padding: '12px 14px',
          fontFamily: 'var(--pn-doc)', color: DOC.ink, background: '#fff',
        }}>
          <div style={{ fontSize: 7.5, color: DOC.mut }}>Процессы / Корзина</div>
          <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2, letterSpacing: '-.02em', margin: '4px 0 7px' }}>
            {ARTIFACT}
          </div>
          <p style={{ fontSize: 8, lineHeight: 1.62, margin: '0 0 6px' }}>
            Корзина живёт 24 часа с последнего изменения. По истечении срока позиции
            удаляются, резерв остатков снимается автоматически.
          </p>
          <div style={{ fontSize: 9, fontWeight: 600, margin: '6px 0 4px' }}>Что зависит от срока</div>
          {['Резерв остатков на складе', 'Расчёт стоимости доставки', 'Срок действия промокодов'].map((t) => (
            <div key={t} style={{ display: 'flex', gap: 5, fontSize: 8, lineHeight: 1.55 }}>
              <span style={{ color: DOC.mut }}>•</span><span>{t}</span>
            </div>
          ))}
          <div style={{ fontSize: 9, fontWeight: 600, margin: '8px 0 4px' }}>История изменений</div>
          <div style={{ fontSize: 8, lineHeight: 1.55, color: DOC.mut }}>
            2024-11 — зафиксирован срок 24 часа<br />2025-03 — уточнено снятие резерва
          </div>
          {/* Абзац, который в финале обновит агент. */}
          <div className="dm-kb-diff" style={{
            fontSize: 8, lineHeight: 1.55, padding: '4px 6px', borderRadius: 4,
            background: 'rgba(61,165,92,.12)', borderLeft: '2px solid rgba(61,165,92,.55)',
            color: '#1f3a29',
          }}>
            <span style={{ fontWeight: 600 }}>Обновлено: </span>
            срок хранения — 30 дней; резерв снимается на 7-й день, позиции остаются.
            <span style={{ display: 'block', fontSize: 7, color: '#2a7440', marginTop: 2 }}>
              правка агента · SHOP-214
            </span>
          </div>
        </div>
      </Window>

      {/* PR появляется последним — когда доска хода работы уже схлопнулась в
          задачу и место под страницей задачи освободилось. */}
      <PrCard />

      {/* ══ ДОСКА ХОДА РАБОТЫ АГЕНТА ═══════════════════════════════════════
          Агент не только меняет код: он заводит СВОЮ ОТДЕЛЬНУЮ доску под
          страницей задачи и выкладывает на неё ход работы, рассуждения, разбор
          артефактов, обновлённую карту историй и фрагмент документации, который
          поправил в Базе знаний. Наполнив её, он схлопывает доску в задачу —
          вложением «Ход работы агента». */}
      <Window zone={ZONES.board2} cls="dm-w-board2">
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'radial-gradient(rgba(18,36,29,.13) 1.2px, transparent 1.4px)',
          backgroundSize: '17px 17px',
        }} />

        <RepCol left={24} top={14} w={420} title="ХОД РАБОТЫ">
          <RepStep cls="dm-r-s1" top={0} n={1}>Прочитал задачу и три приложенных артефакта</RepStep>
          <RepStep cls="dm-r-s2" top={38} n={2}>Нашёл по смыслу исходный запрос клиента и переписку поддержки</RepStep>
          <RepStep cls="dm-r-s3" top={90} n={3}>Проверил зависимости: резерв остатков, доставка, промокоды</RepStep>
          <RepStep cls="dm-r-s4" top={142} n={4}>Реализовал TTL корзины 30 дней и снятие резерва на 7-й день</RepStep>
          <RepStep cls="dm-r-s5" top={194} n={5}>Обновил документацию и карту историй</RepStep>
        </RepCol>

        <RepCol left={470} top={14} w={400} title="ХОД РАССУЖДЕНИЙ">
          <Sticky cls="dm-r-n1" left={0} top={0} w={400} h={64} tone="shell">
            30 дней держать весь резерв нельзя — склад встанет. Значит, разделяем: корзина живёт, резерв снимается на 7-й день.
          </Sticky>
          <Sticky cls="dm-r-n2" left={0} top={74} w={400} h={56} tone="gold">
            82% корзин живут меньше суток — правка затронет 18% и не нагрузит базу.
          </Sticky>
          <Sticky cls="dm-r-n3" left={0} top={140} w={400} h={56} tone="green">
            Промокоды привязаны к дате — добавил проверку срока при возврате в корзину.
          </Sticky>
        </RepCol>

        <RepCol left={24} top={290} w={300} title="АРТЕФАКТЫ: ЧТО ВЗЯЛ И ЧТО НЕТ">
          <RepVerdict cls="dm-r-v1" top={0} ok label={ARTIFACT} note="из задачи" />
          <RepVerdict cls="dm-r-v2" top={30} ok label="Данные · срок жизни корзин" note="из задачи" />
          <RepVerdict cls="dm-r-v3" top={60} ok label="Запрос клиента · 6 мес назад" note="нашёл сам" />
          <RepVerdict cls="dm-r-v4" top={90} ok label="Поддержка · 23 обращения" note="нашёл сам" />
          <RepVerdict cls="dm-r-v5" top={120} ok={false} label="Регламент хранения v1" note="устарел" />
        </RepCol>

        <RepCol left={350} top={290} w={320} title="КАРТА ИСТОРИЙ · ОБНОВЛЕНА">
          <RepUsm cls="dm-r-usm" newCls="dm-r-usm-new" />
        </RepCol>

        <RepCol left={694} top={290} w={182} title="ДОКУМЕНТАЦИЯ В БЗ">
          <div className="dm-r-doc" style={{
            position: 'relative', padding: '7px 9px', background: '#fff',
            border: `1px solid ${LINE}`, borderRadius: 7, fontFamily: 'var(--pn-doc)', color: DOC.ink,
          }}>
            <div style={{ fontSize: 8, color: DOC.mut }}>Процессы / Корзина</div>
            <div style={{ fontSize: 10.5, fontWeight: 600, margin: '3px 0 5px' }}>{ARTIFACT}</div>
            <div style={{ fontSize: 8, lineHeight: 1.5, color: DOC.mut, textDecoration: 'line-through' }}>
              Корзина живёт 24 часа с последнего изменения.
            </div>
            <div className="dm-r-doc-add" style={{
              fontSize: 8, lineHeight: 1.5, marginTop: 4, padding: '4px 6px', borderRadius: 4,
              background: 'rgba(61,165,92,.12)', borderLeft: '2px solid rgba(61,165,92,.55)', color: '#1f3a29',
            }}>
              Корзина живёт 30 дней. Резерв остатков снимается на 7-й день, позиции сохраняются.
            </div>
          </div>
        </RepCol>
      </Window>

      {/* ── Один объект в хранении ────────────────────────────────────────
             Появляется одним тактом, после того как синяя обводка уже показала
             три вхождения задачи. Пунктиры сводят их в одну запись, узел её
             называет — и уходит: объяснение нужно ровно один раз. */}
      <svg
        className="dm-store-links"
        style={{ position: 'absolute', left: 0, top: 0, width: WORLD.w, height: WORLD.h, pointerEvents: 'none' }}
        viewBox={`0 0 ${WORLD.w} ${WORLD.h}`}
      >
        {STORE_LINKS.map(({ zone, dx, dy, railX }) => {
          const z = ZONES[zone];
          const sx = z.x + dx;
          const sy = z.y + dy;
          const tx = ZONES.store.x + ZONES.store.w / 2;
          const ty = ZONES.store.y;
          // Боковой вынос и вертикальный спуск по зазору: прямая кривая из
          // строки бэклога прошла бы наискось через весь список.
          const d = railX
            ? `M ${sx} ${sy} L ${railX} ${sy} L ${railX} ${ty - 60}`
              + ` C ${railX} ${ty - 20}, ${tx} ${ty - 20}, ${tx} ${ty}`
            : `M ${sx} ${sy} C ${sx} ${(sy + ty) / 2}, ${tx} ${(sy + ty) / 2}, ${tx} ${ty}`;
          return (
            <path key={zone} d={d} fill="none" stroke={TASKS.accent}
                  strokeWidth={1.6} strokeDasharray="6 6" opacity={0.6} />
          );
        })}
      </svg>

      <div className="dm-store" style={{
        position: 'absolute', left: ZONES.store.x, top: ZONES.store.y,
        width: ZONES.store.w, height: ZONES.store.h,
        background: '#101613', borderRadius: 16, border: '1px solid rgba(255,255,255,.10)',
        padding: '16px 20px', color: '#f2f3ef',
        boxShadow: '0 30px 60px -28px rgba(6,12,10,.7)',
      }}>
        <div style={{ fontSize: 11, letterSpacing: '.14em', color: '#8ea69a', fontWeight: 600 }}>
          ПРЕДСТАВЛЕНИЙ МНОГО, А ОБЪЕКТ ОДИН
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 7 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: TASKS.muted, flex: 'none' }} />
          <span style={{ fontSize: 16, fontWeight: 600 }}>SHOP-214 · Хранить корзину 30 дней</span>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 11 }}>
          {['граф', 'вектор', 'пространство'].map((f) => (
            <span key={f} style={{
              fontSize: 11, padding: '3px 10px', borderRadius: 999,
              background: 'rgba(255,255,255,.08)', color: '#cfd8d2',
            }}>{f}</span>
          ))}
        </div>
      </div>

      {/* ── Сеть MCP: шина от шильдика и отводы в разделы ─────────────────
             Пока агент пишет ход рассуждений, камера стоит на нём, а от MCP
             ветка за веткой прорастает сеть: спуск от шильдика, горизонтальная
             шина в зазоре под окнами и отводы вверх — в страницу задачи, в
             ярлык Базы знаний, в кнопку чата, в саму доску. В кадре агента
             видно, как нити уходят влево за край; куда они пришли, показывает
             отъезд камеры в конце акта.
             Байты бегут ОТДЕЛЬНОЙ линией поверх рельса: рельс рисуется один раз
             (stroke-dashoffset от длины к нулю), поток штрихов крутится вечно —
             одной анимацией это не разделить. */}
      <svg
        style={{ position: 'absolute', left: 0, top: 0, width: WORLD.w, height: WORLD.h, pointerEvents: 'none' }}
        viewBox={`0 0 ${WORLD.w} ${WORLD.h}`}
      >
        {NET_TAPS.map((tap) => {
          const d = netPath(tap);
          return (
            <g key={tap.rail}>
              <path className={tap.rail} d={d} pathLength={100} fill="none"
                    stroke={NET_INK} strokeWidth={2.4} strokeLinecap="round"
                    strokeDasharray="100" opacity={0} />
              <path className={tap.pack} d={d} fill="none"
                    stroke={NET_INK} strokeWidth={3.6} strokeLinecap="round"
                    strokeDasharray="4 16" opacity={0} />
              <circle className={tap.dot} cx={tap.to ?? tap.x} cy={tap.y} r={5}
                      fill={NET_INK} opacity={0} />
            </g>
          );
        })}
      </svg>

      {/* ── Шильдик MCP и линия к агенту ─────────────────────────────────── */}
      <svg
        style={{ position: 'absolute', left: 0, top: 0, width: WORLD.w, height: WORLD.h, pointerEvents: 'none' }}
        viewBox={`0 0 ${WORLD.w} ${WORLD.h}`}
      >
        <path
          className="dm-mcp-line"
          d={`M ${MCP_GATE.x + MCP_GATE.w} ${MCP_GATE.y + MCP_GATE.h / 2} L ${ZONES.agent.x} ${MCP_GATE.y + MCP_GATE.h / 2}`}
          fill="none" stroke="#63c69e" strokeWidth={1.6} strokeDasharray="6 6"
        />
      </svg>
      <div className="dm-mcp" style={{
        position: 'absolute', left: MCP_GATE.x, top: MCP_GATE.y, width: MCP_GATE.w, height: MCP_GATE.h,
        background: '#101613', borderRadius: 999, color: '#63c69e',
        fontSize: 10, fontWeight: 700, letterSpacing: '.12em',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        MCP
      </div>

      {/* ── Агент: снаружи рамки ──────────────────────────────────────────── */}
      <div className="dm-agent" style={{
        position: 'absolute', left: ZONES.agent.x, top: ZONES.agent.y,
        width: ZONES.agent.w, height: ZONES.agent.h,
        background: '#101613', borderRadius: 20, border: '1px solid rgba(255,255,255,.10)',
        // Тень плотная: на общем плане окно агента должно читаться как первый
        // план перед рамкой, а не как ещё одна плитка того же слоя.
        boxShadow: '0 40px 80px -30px rgba(6,12,10,.75)',
        padding: 22, color: '#f2f3ef',
      }}>
        {/* Кегли ЗДЕСЬ КРУПНЕЕ, чем в продуктовых окнах: агент выходит на первый
            план не приближением камеры, а собственным размером — масштаб кадра
            в этот момент тот же, что и в предыдущей сцене. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 22 }}>
          <Ava src="/claude-code-icon.svg" size={30} />
          <span style={{ fontSize: 24, fontWeight: 600 }}>{ZONES.agent.title}</span>
          <span style={{ fontSize: 14, color: '#8ea69a', marginLeft: 'auto' }}>снаружи · через MCP</span>
        </div>
        {/* Восемь строк вместо семи — добавились «подключаюсь к разделам» (она
            объясняет прорастающую сеть) и «фиксирую ход работы», по которой
            камера уходит на доску отчёта. Кегль и отбивки поджаты: восьмая
            строка иначе не помещалась в окно. */}
        {[
          'Читаю задачу и приложенные артефакты',
          'Подключаюсь к разделам пространства через MCP',
          'Ищу связанные обсуждения по смыслу, не по словам',
          'Нашёл запрос клиента — 6 месяцев назад',
          'Нашёл обсуждение с поддержкой, 23 обращения',
          'Регламент хранения v1 устарел — не беру',
          'Реализовал: TTL корзины 30 дней, обновил документацию',
          'Фиксирую ход работы на доске',
        ].map((s, i) => (
          <div key={i} className={`dm-ag-${i + 1}`} style={{
            fontSize: 16.5, lineHeight: 1.4, padding: '10px 14px', marginBottom: 9,
            background: 'rgba(255,255,255,.06)', borderRadius: 11, color: '#cfd8d2',
          }}>{s}</div>
        ))}
      </div>
    </>
  );
  if (!animated) return body;
  return (
    // `text-align: left` — НЕ косметика: лендинг центрирует текст по всей
    // странице, и в герое вся вёрстка окон (стикеры, строки бэклога, тело
    // документа) вставала по центру. Сцена изображает интерфейс и обязана
    // жить в своей типографике, а не наследовать страницу-носитель.
    <div className="dm-scene" style={{ position: 'absolute', inset: 0, textAlign: 'left' }}>
      <style>{TIMELINE_CSS + LOADER_CSS}</style>
      {body}
    </div>
  );
}
